// Thème 2027 : la palette de l'année de WGSN / Coloro (Luminous Blue, Pop Pink, Energy Orange — un orange
// vintage Y2K — avec un vert prairie pour le succès) sur un bleu nuit, pour un public de joueurs sur mobile, familial et plutôt jeune : des
// couleurs vives et chaleureuses, rien de lugubre (même le cimetière devient un crépuscule lavande).
// Les couleurs de l'interface (CSS) sont dans src/styles/themes.css, bloc :root / [data-theme='2027'].
export const luminousGameTheme = Object.freeze({
  title: 'ZERO KAMI',
  // Les couleurs des kamis suivent leur portrait (halo du viseur, tuiles de galerie) : inchangées d'un thème à l'autre
  ghostColors: [
    '#f28fb0', '#7ec8e3', '#ffd452', '#e8973d', '#bcd9ff', '#b28ee0', '#f4e08a', '#9aa3c9',
    '#f0923c', '#c49a6c', '#f6c98a', '#7dd3ae', '#7a5ea8', '#8a7ff0', '#a9e0ea',
  ],
  worldBorderColor: '#ff6fb1',
  // Charte de l'interface (les noms gardent ceux du thème Halloween : pumpkin = couleur principale…)
  pumpkin: '#4f7dff',        // Luminous Blue (l'orange des boutons est dans styles/themes.css)
  witchPurple: '#ff6fb1',    // Pop Pink
  toxicGreen: '#5fd07b',     // Meadowland Green
  pumpkinGold: '#ff7a45',    // Energy Orange (compteur « Cherche »)
  appBg: '#090d24',
  panelBg: '#0f1535',
  panelBorder: '#2b3a7a',
});

// Décors du viseur, un par niveau : mêmes rôles de couleur que le thème Halloween (voir halloween.js).
const BASE_GROUND = {
  hollow: '#0f1642', main: '#2c3f8e', accent: '#8fa6e8', glow: 'rgba(255, 154, 92, 0.9)', cool: '#8fe8ff',
  lava: '#ff5f2e', deep: '#0a0f33', shadow: 'rgba(0, 0, 0, 0.38)',
};
const ground = (overrides) => ({ ...BASE_GROUND, ...overrides });

const BASE_PALETTE = {
  ground: BASE_GROUND,
  far: '#1a2260', farAccent: '#1a2260', farGlow: 'rgba(255, 154, 92, 0.6)',
  near: '#070a24', nearAccent: '#070a24', nearGlow: 'rgba(255, 154, 92, 0.8)',
  body: '#fff3c4', bodyHalo: 'rgba(255, 243, 196, 0.35)', bodyHaloEdge: 'rgba(255, 243, 196, 0)',
  bodyShade: 'rgba(0, 0, 0, 0.08)',
  star: '#eef3ff', flyer: '#0b1030',
  horizonLine: 'rgba(255, 154, 92, 0.4)',
  fog: null, fogEdge: null, fogWisps: false,
  sea: null,
};

export const luminousSceneryPalettes = Object.freeze({
  // Heure bleue sur la ville : de l'indigo au bleu lumineux, puis rose et orange à l'horizon
  city: {
    ...BASE_PALETTE,
    skyTop: '#0b1240', skyMid: '#2b3fa8', skyGlow: '#e0508f', skyHorizon: '#ff9a5c',
    groundTop: '#101a44', groundBottom: '#04061a',
  },
  // Coucher de soleil sur la plage : ciel bleu-violet et rose, mer orangée, sable argile
  beach: {
    ...BASE_PALETTE,
    skyTop: '#14206a', skyMid: '#5a3fb8', skyGlow: '#ff6fb1', skyHorizon: '#ffb86b',
    groundTop: '#4a2f4e', groundBottom: '#150c26',
    ground: ground({ hollow: '#5c3a46', main: '#a86a58', accent: '#f0c4a0', glow: 'rgba(255, 210, 130, 0.95)', cool: '#7fe8e8', deep: '#2a1a34' }),
    far: '#2a2a78', near: '#0e1240', nearGlow: 'rgba(255, 200, 130, 0.85)',
    body: '#ffd98a', bodyHalo: 'rgba(255, 200, 130, 0.5)', bodyHaloEdge: 'rgba(255, 200, 130, 0)',
    flyer: '#1c2060',
    horizonLine: 'rgba(255, 225, 180, 0.45)',
    sea: { top: '#ff8f6b', bottom: '#2a2f8a', glint: 'rgba(255, 230, 170, 0.75)' },
  },
  // Nuit bleue en forêt : pleine lune, étoiles, vert prairie
  forest: {
    ...BASE_PALETTE,
    skyTop: '#050b2c', skyMid: '#102a6a', skyGlow: '#1f5fa8', skyHorizon: '#3f86c8',
    groundTop: '#0c2a24', groundBottom: '#03120f',
    ground: ground({ hollow: '#0a2a20', main: '#2f7a55', moss: '#4fa860', accent: '#9fe0b0', glow: '#e6ff7a', cool: '#7fe0ff', deep: '#072018' }),
    far: '#0c2a4a', near: '#031a14', nearGlow: '#e6ff7a',
    body: '#f4f7ff', bodyHalo: 'rgba(200, 220, 255, 0.3)', bodyHaloEdge: 'rgba(200, 220, 255, 0)',
    bodyShade: 'rgba(90, 110, 150, 0.18)',
    horizonLine: 'rgba(140, 190, 240, 0.22)',
    fog: 'rgba(150, 190, 230, 0.12)', fogEdge: 'rgba(150, 190, 230, 0)',
  },
  // Crépuscule alpin : sommets bleu pervenche aux neiges claires, chalets éclairés
  mountains: {
    ...BASE_PALETTE,
    skyTop: '#1a2878', skyMid: '#4f58c8', skyGlow: '#ff7fb8', skyHorizon: '#ffc58a',
    groundTop: '#1c2468', groundBottom: '#080c30',
    ground: ground({ hollow: '#1a2058', main: '#4a5aa8', petal: '#ffffff', accent: '#c8d4ff', glow: 'rgba(255, 197, 138, 0.9)', cool: '#bcd0ff', deep: '#121847' }),
    far: '#3a4aa0', farAccent: '#e6ecff', farGlow: 'rgba(255, 197, 138, 0.7)',
    near: '#0b1040', nearGlow: 'rgba(255, 190, 110, 0.85)',
    body: '#fff0b8', bodyHalo: 'rgba(255, 210, 150, 0.45)', bodyHaloEdge: 'rgba(255, 210, 150, 0)',
    flyer: '#1c2468',
    horizonLine: 'rgba(255, 197, 138, 0.3)',
  },
  // Jungle tropicale : ciel turquoise et vert prairie, soleil doré, brume claire
  jungle: {
    ...BASE_PALETTE,
    skyTop: '#0a3a4a', skyMid: '#1f8a62', skyGlow: '#ff8a4a', skyHorizon: '#ffd070',
    groundTop: '#0f3a22', groundBottom: '#04180d',
    ground: ground({ hollow: '#0a2e1a', main: '#2f8a4a', root: '#8a5a3a', leaf: '#6fcf7a', accent: '#bde06a', glow: '#ffd24a', cool: '#7affd0', deep: '#0a2414' }),
    far: '#166a46', near: '#04200f', nearAccent: '#d4602a', nearGlow: '#ffd24a',
    body: '#ffe08a', bodyHalo: 'rgba(255, 210, 120, 0.4)', bodyHaloEdge: 'rgba(255, 210, 120, 0)',
    flyer: '#0a3020',
    horizonLine: 'rgba(255, 208, 112, 0.3)',
    fog: 'rgba(190, 240, 190, 0.12)', fogEdge: 'rgba(190, 240, 190, 0)',
  },
  // Crépuscule lavande sur le vieux cimetière : bleu nuit, horizon rose, lueurs vert doux, brume claire
  graveyard: {
    ...BASE_PALETTE,
    skyTop: '#0a0a30', skyMid: '#2a2a78', skyGlow: '#7a3a8a', skyHorizon: '#c2528a',
    groundTop: '#14143a', groundBottom: '#05051a',
    ground: ground({ hollow: '#101038', main: '#3a3a7a', stone: '#6a6aa8', accent: '#d4d8f4', glow: '#8fffb0', cool: '#8fe8ff', deep: '#0a0a2c' }),
    far: '#1c1c5a', farGlow: 'rgba(143, 255, 176, 0.55)', near: '#060620', nearGlow: '#8fffb0',
    body: '#f2f6d8', bodyHalo: 'rgba(220, 255, 220, 0.28)', bodyHaloEdge: 'rgba(220, 255, 220, 0)',
    bodyShade: 'rgba(100, 110, 150, 0.2)',
    flyer: '#060620',
    horizonLine: 'rgba(194, 82, 138, 0.35)',
    fog: 'rgba(180, 190, 230, 0.16)', fogEdge: 'rgba(180, 190, 230, 0)', fogWisps: true,
  },
});
